import { useLazyQuery, useQuery } from '@apollo/client'
import { Settings } from '@mui/icons-material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { Ordering, SnapshotType } from 'graphql/generated-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  DisplayCoolerProductHoverStatsQuery,
  DisplayCoolerProductHoverStatsQueryVariables,
  GET_DISPLAY_COOLER_PRODUCT_HOVER_STATS,
  GET_DISPLAY_SNAPSHOTS,
  GET_LATEST_DISPLAY_PLANOGRAM_CHANGE,
  GetDisplaySnapshotsQuery,
  GetDisplaySnapshotsQueryVariables,
  GetLatestDisplayPlanogramChangeQuery,
  GetLatestDisplayPlanogramChangeQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import RenderedPlanogram from '@/pages/planogram/components/RenderedPlanogram'
import { getFullSizeImage } from '@/utils/image.helpers'

import useProductsByUpc from '../hooks/useProductsByUpc'
import { getTrendFacingHighlight } from '../utils/cooler-detail-formatters'
import CoolerProductHoverCard from './CoolerProductHoverCard'
import DisplaySettingsDialog from './DisplaySettingsDialog'
import HeatmapLegend from './HeatmapLegend'
import PlanogramRestockStatus from './PlanogramRestockStatus'

type Props = {
  displayId: string
  shapedUpcs: string[][][]
  heatMapValuesByUpc: Record<string, number>
  productStatsByUpc: Record<
    string,
    {
      name: string | null
      revenue: number | null
      revenueTrend: number | null
    }
  >
  startDate: string
  endDate: string
  hoveredProductUpc?: string | null
  planogramVerticalAlign?: 'center' | 'flex-start'
  fitPlanogramToWidth?: boolean
}

const panelSx = {
  p: 2,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const CoolerPlanRealPanel: React.FC<Props> = ({
  displayId,
  shapedUpcs,
  heatMapValuesByUpc,
  productStatsByUpc,
  startDate,
  endDate,
  hoveredProductUpc,
  planogramVerticalAlign = 'center',
  fitPlanogramToWidth = false,
}) => {
  const { t } = useT()
  const navigate = useNavigate()
  const { id: storeId } = useParams()
  const [mode, setMode] = useState<'plan' | 'real'>('plan')
  const [showHeatmap, setShowHeatmap] = useState(false)
  const [averageDepthDialogOpen, setAverageDepthDialogOpen] = useState(false)
  const [hoveredFacingUpc, setHoveredFacingUpc] = useState<string | null>(null)
  const planogramViewportRef = useRef<HTMLDivElement>(null)
  const prefetchedHoverKeys = useRef<Set<string>>(new Set())
  const [planogramMaxHeight, setPlanogramMaxHeight] = useState<number>()
  const hasPlanogram = Boolean(shapedUpcs?.[0]?.length)
  const upcsInPlan = useMemo(
    () =>
      Array.from(
        new Set(shapedUpcs?.[0]?.flatMap(shelf => shelf).filter(Boolean) || []),
      ).sort(),
    [shapedUpcs],
  )
  const { loading: productsLoading, productImageByUpc } =
    useProductsByUpc(upcsInPlan)

  const { data, loading, error, refetch } = useQuery<
    GetDisplaySnapshotsQuery,
    GetDisplaySnapshotsQueryVariables
  >(GET_DISPLAY_SNAPSHOTS, {
    variables: {
      filters: {
        display: { id: { exact: displayId } },
        type: SnapshotType.POST_RESTOCK,
      },
      order: { captureTime: Ordering.DESC },
      pagination: { limit: 1, offset: 0 },
    },
    fetchPolicy: 'cache-and-network',
  })
  const { data: changeData } = useQuery<
    GetLatestDisplayPlanogramChangeQuery,
    GetLatestDisplayPlanogramChangeQueryVariables
  >(GET_LATEST_DISPLAY_PLANOGRAM_CHANGE, {
    variables: { displayId },
    fetchPolicy: 'cache-and-network',
  })
  const [prefetchHoverStats] = useLazyQuery<
    DisplayCoolerProductHoverStatsQuery,
    DisplayCoolerProductHoverStatsQueryVariables
  >(GET_DISPLAY_COOLER_PRODUCT_HOVER_STATS, {
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    prefetchedHoverKeys.current.clear()
  }, [displayId, startDate, endDate])

  useEffect(() => {
    if (fitPlanogramToWidth || !planogramViewportRef.current) return

    const element = planogramViewportRef.current
    const updateHeight = () => setPlanogramMaxHeight(element.clientHeight || 0)
    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)

    return () => observer.disconnect()
  }, [fitPlanogramToWidth, hasPlanogram])

  const plan = useMemo(
    () =>
      shapedUpcs?.[0]?.map(shelf =>
        shelf.map(upc => ({
          upcA: upc,
          preferredName: productImageByUpc[upc]?.preferredName || null,
          planogramImageUrl: productImageByUpc[upc]?.planogramImageUrl || null,
          marketingImageUrl: productImageByUpc[upc]?.marketingImageUrl || null,
          packaging: productImageByUpc[upc]?.packaging ?? null,
        })),
      ) || [],
    [productImageByUpc, shapedUpcs],
  )

  const loadingByUpc = useMemo(
    () =>
      Object.fromEntries(
        upcsInPlan.map(upc => [
          upc,
          productsLoading && !productImageByUpc[upc],
        ]),
      ) as Record<string, boolean>,
    [productImageByUpc, productsLoading, upcsInPlan],
  )

  const highlightByUpc = useMemo(() => {
    const min = Math.min(
      ...Object.values(heatMapValuesByUpc).map(stat => stat ?? 0),
    )
    const max = Math.max(
      ...Object.values(heatMapValuesByUpc).map(stat => stat ?? 0),
    )
    return Object.fromEntries(
      Object.entries(heatMapValuesByUpc).map(([upc, value]) => [
        upc,
        getTrendFacingHighlight(value, min, max),
      ]),
    )
  }, [heatMapValuesByUpc])
  const effectiveHighlightByUpc = useMemo(() => {
    if (hoveredFacingUpc) {
      return Object.fromEntries(
        upcsInPlan.map(upc => {
          const base = showHeatmap ? highlightByUpc[upc] || {} : {}

          if (upc === hoveredFacingUpc) {
            return [
              upc,
              {
                ...base,
                bgColor: 'transparent',
                borderColor: base.borderColor || 'rgba(33, 33, 33, 0.88)',
              },
            ]
          }

          return [upc, base]
        }),
      )
    }

    if (!hoveredProductUpc) {
      return showHeatmap ? highlightByUpc : {}
    }

    const hoveredOverride = {
      bgColor: 'rgba(21, 101, 192, 0.22)',
      borderColor: 'rgba(13, 71, 161, 0.95)',
    }

    return Object.fromEntries(
      upcsInPlan.map(upc => {
        if (upc === hoveredProductUpc) {
          const base = showHeatmap ? highlightByUpc[upc] || {} : {}
          return [
            upc,
            {
              ...base,
              ...hoveredOverride,
            },
          ]
        }
        if (showHeatmap) {
          return [upc, highlightByUpc[upc] || {}]
        }
        return [upc, {}]
      }),
    )
  }, [
    highlightByUpc,
    hoveredFacingUpc,
    hoveredProductUpc,
    showHeatmap,
    upcsInPlan,
  ])

  const latestImageUrl = data?.tenantDisplaySnapshots.results[0]?.images[0]?.url
  const latestSnapshot = data?.tenantDisplaySnapshots.results[0]
  const latestChange = changeData?.displayPlanogramHistory?.[0]
  const completedBy = latestChange?.completedBy
    ? `${latestChange.completedBy.firstName} ${latestChange.completedBy.lastName}`.trim()
    : null
  const snapshotDate = latestSnapshot?.captureTime || latestSnapshot?.created
  const snapshotIsOutdated = Boolean(
    latestChange?.created &&
      snapshotDate &&
      new Date(snapshotDate) < new Date(latestChange.created),
  )
  const displayRow = data?.tenantDisplaySnapshots.results[0]?.display
  const productsPerFacing = displayRow?.averageDepth ?? 10
  const displayName = displayRow?.name ?? ''
  const handleFacingHover = (upc: string) => {
    setHoveredFacingUpc(upc)
    const key = `${displayId}:${upc}:${startDate}:${endDate}`
    if (prefetchedHoverKeys.current.has(key)) return
    prefetchedHoverKeys.current.add(key)

    prefetchHoverStats({
      variables: {
        displayId,
        upc,
        startDate,
        endDate,
      },
    })
  }

  const handleEditClick = () => {
    if (!storeId) return
    navigate(`/stores/${storeId}/planograms/${displayId}`)
  }

  return (
    <Paper
      sx={{
        ...panelSx,
        ...(fitPlanogramToWidth && {
          height: 'auto',
          overflow: 'visible',
        }),
      }}
    >
      {storeId ? (
        <DisplaySettingsDialog
          open={averageDepthDialogOpen}
          onClose={() => setAverageDepthDialogOpen(false)}
          displayId={displayId}
          storeId={storeId}
          displayName={displayName}
          currentAverageDepth={productsPerFacing}
          onSaved={() => {
            void refetch()
          }}
        />
      ) : null}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            minWidth: 0,
          }}
        >
          {mode === 'plan' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip
                title={
                  showHeatmap
                    ? t('coolerPlanRealPanel.toggleHeatmap.hideMessage')
                    : t('coolerPlanRealPanel.toggleHeatmap.showMessage')
                }
              >
                <IconButton
                  size="small"
                  onClick={() => setShowHeatmap(prev => !prev)}
                >
                  {showHeatmap ? (
                    <VisibilityIcon fontSize="small" />
                  ) : (
                    <VisibilityOffIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                Revenue Heatmap
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, value) => {
              if (value) setMode(value)
            }}
            size="small"
          >
            <ToggleButton value="plan">Plan</ToggleButton>
            <ToggleButton value="real">Real</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            minWidth: 0,
          }}
        >
          {storeId && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={t('coolerPlanRealPanel.settings.tooltip')}>
                <IconButton
                  size="small"
                  onClick={() => setAverageDepthDialogOpen(true)}
                >
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('coolerPlanRealPanel.editPlanogram.tooltip')}>
                <IconButton size="small" onClick={handleEditClick}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <PlanogramRestockStatus
          changedAt={latestChange?.created}
          completedAt={latestChange?.completedAt}
          completedBy={completedBy}
        />
        {mode === 'real' && snapshotIsOutdated && (
          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
            {t(
              'coolerPlanRealPanel.snapshotOutdated',
              'This snapshot was taken before the current planogram was created.',
            )}
          </Alert>
        )}
      </Box>

      {showHeatmap && mode === 'plan' && <HeatmapLegend sx={{ mt: 2 }} />}

      {mode === 'plan' ? (
        plan.length ? (
          <Box
            ref={planogramViewportRef}
            sx={{
              flex: fitPlanogramToWidth ? 'none' : 1,
              minHeight: 0,
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              pt: 1,
            }}
          >
            <Box
              sx={{
                flex: fitPlanogramToWidth ? 'none' : 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: planogramVerticalAlign,
              }}
            >
              <RenderedPlanogram
                products={plan}
                highlightByUpc={effectiveHighlightByUpc}
                loadingByUpc={loadingByUpc}
                onFacingHover={handleFacingHover}
                onFacingLeave={() => setHoveredFacingUpc(null)}
                maxHeight={
                  !fitPlanogramToWidth && planogramMaxHeight
                    ? planogramMaxHeight - 8
                    : undefined
                }
                renderPopoverContent={upc => (
                  <CoolerProductHoverCard
                    displayId={displayId}
                    upc={upc}
                    productsPerFacing={productsPerFacing}
                    numFacings={
                      plan.flat().filter(facing => facing.upcA === upc).length
                    }
                    startDate={startDate}
                    endDate={endDate}
                    initialInfo={{
                      preferredName:
                        productImageByUpc[upc]?.preferredName || null,
                      revenue: productStatsByUpc[upc]?.revenue ?? null,
                      revenueTrend:
                        productStatsByUpc[upc]?.revenueTrend ?? null,
                      imageUrl:
                        productImageByUpc[upc]?.marketingImageUrl ||
                        productImageByUpc[upc]?.planogramImageUrl ||
                        null,
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary">No planogram available</Typography>
        )
      ) : loading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error.message}</Typography>
      ) : latestImageUrl ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={getFullSizeImage(latestImageUrl)}
            alt={t('coolerPlanRealPanel.latestSnapshot.alt')}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>
      ) : (
        <Typography color="text.secondary">
          {t(
            'coolerPlanRealPanel.noSnapshotAvailable',
            'No snapshot available',
          )}
        </Typography>
      )}
    </Paper>
  )
}

export default CoolerPlanRealPanel
